import { Component, Input, OnInit } from "@angular/core";
import { TableSummaryCalculatorFunction } from "@app/directives/_layout/v-table/_components/visualisations/table-summary-box/table-summary-box.component";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { AsyncObservableComponent } from "@app/directives/_misc/async-observable/async-observable.component";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { VerticalFilterItemCreationCallback } from "@app/directives/_resourceFilters/vertical-filter-list/vertical-filter-list.component";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IUser } from "@app/interfaces/IUser";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IAccount } from "@app/services/account.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, forkJoin } from "rxjs";
import { EngineerCommissionStatus, EngineerJobItemType } from "../../foundation.enums";
import { IEngineerCommission } from "../../foundation.interfaces";
import { FoundationNavigationModule } from "../../foundation.navigation";
import { IReseller } from "../../_models/IReseller";
import { AccountService } from "../../_services/account.service";
import { EngineerService } from "../../_services/engineer.service";
import { VehicleValidationCheckRecordService } from "../../_services/vehicle-validation-check-record.service";
import { FoundationVehiclesService } from "../../_services/vehicle.service";
import './commission-list.component.scss';
import { CommissionService } from "./commission.service";

@UntilDestroy()
@Component({
    selector: 'commission-list',
    template: require('./commission-list.component.html')
})
export class CommissionListComponent implements OnInit {
    @Input() customerId: number;

    commissions: IEngineerCommission[] = [];
    vehiclesMap: Map<number, IVehicle>;
    accountsMap: Map<number, IAccount>;
    resellerMap: Map<number, IReseller>;
    engineersMap: Map<number, IUser>;

    tabCount: number = 0;

    defaultDateRange: DateRange = DateRange.Latest4Weeks;
    start: Date;
    end: Date;
    loading: boolean;

    filterTabs: IFilterBarTab<IEngineerCommission>[];

    tableColumns: IVTableColumn<IEngineerCommission>[];
    tableRows: IVTableRow[];

    constructor(
        private readonly accountService: AccountService,
        private readonly commissionService: CommissionService,
        private readonly engineerService: EngineerService,
        private readonly filterBarService: FilterBarService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: FoundationVehiclesService,
        private readonly vehicleValidationCheckRecordService: VehicleValidationCheckRecordService,
        private readonly vTableService: VTableService) { }

    ngOnInit(): void {
        this.customerId = this.navigationService.getRouteParam("customerId");
        this.createTable();
        this.createTabs();
    }

    onFilterChanged(event: IAggregateFilterEvent<IEngineerCommission>): void {
        if (event.trigger === 'date') {
            this.start = event.date[0];
            this.end = event.date[1];
            this.loadData();
        }
    }

    onRowClicked(row: IVTableRow): void {
        const commissionId = row.data.Id;
        this.navigationService.go(FoundationNavigationModule.ViewCommission, { commissionId })
    }

    newCommission(): void {
        this.navigationService.go(FoundationNavigationModule.NewCommissionWithBindings, { jobItemId: null, customerId: this.customerId || null });
    }

    resellerItemCreationCallback: VerticalFilterItemCreationCallback<IEngineerCommission> = (commission) => {

        return {
            name: this.getResellerName(commission),
            data: commission,
            predicate: (c) => c.account.ResellerId === commission.account.ResellerId
        };
    }

    visitReasonItemCreationCallback: VerticalFilterItemCreationCallback<IEngineerCommission> = (commission) => {

        return {
            name: commission.VisitReason,
            data: commission,
            predicate: (c) => c.VisitReasonId === commission.VisitReasonId
        };
    }

    validationItemCreationCallback: VerticalFilterItemCreationCallback<IEngineerCommission> = (commission) => {

        return {
            name: commission.VisitReason,
            data: commission,
            predicate: (c) => c.VisitReasonId === commission.VisitReasonId
        };
    }

    repeatVisitsItemCreationCallback: VerticalFilterItemCreationCallback<IEngineerCommission> = (commission) => {

        return {
            name: commission.vehicle?.Reg || "Unknown",
            icon: "directions_car",
            data: commission,
            predicate: (c) => c.VehicleId === commission.VehicleId
        };
    }

    subscriptionsCalculator: TableSummaryCalculatorFunction<IEngineerCommission> = (c) => {
        return c.Id % 0 == 0 ? 1 : 0;
    }

    // ---

    private loadData() {
        this.loading = true;

        combineLatest([
            this.commissionService.getEngineerCommissions({ AccountId: this.customerId, Start: this.start, End: this.end }),
            this.vehicleService.getVehicles(false),
            this.accountService.getCustomerAccounts(),
            this.accountService.getResellers(),
            this.engineerService.getEngineers()
        ])

            .pipe(untilDestroyed(this)).subscribe(([commissions, vehicles, accounts, resellers, engineers]) => {
                this.commissions = commissions;
                this.tabCount = commissions.length;
                this.vehiclesMap = this.vehicleService.createVehicleMap(vehicles);
                this.accountsMap = new Map(accounts.map(account => [account.Id, account]));
                this.resellerMap = new Map(resellers.map(reseller => [reseller.Id, reseller]));
                this.engineersMap = new Map(engineers.map(engineer => [engineer.Id, engineer]));

                this.mapDataToCommissions();
                this.tableRows = this.vTableService.createTableRows(this.commissions, this.tableColumns);

                this.loading = false;

                commissions.forEach(c => {
                    if (c.VisitReasonId == 0) {
                        c.VisitReason = EngineerJobItemType[0];
                    }
                });
            })
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Timestamp", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: commission => this.vTableService.createDateCell(commission.Timestamp, { dateTime: true })
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: commission => this.vTableService.createVehicleRegCell(commission.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Commissions.VisitReason", width: 100, minTableWidth: 900 }),
                createCellFunction: commission => this.vTableService.createTableCell(commission.VisitReason)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Account", minWidth: 10 }),
                createCellFunction: commission => this.vTableService.createTableCell(commission.account.CompanyName)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerUsers.Engineer", width: 200, minTableWidth: 800 }),
                createCellFunction: commission => {
                    const engineer: string = this.engineersMap.get(commission.EngineerUserId)?.Description;

                    return this.vTableService.createTableCell(engineer);
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Reseller", width: 130, minTableWidth: 700 }),
                createCellFunction: commission => this.vTableService.createTableCell(this.getResellerName(commission))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", width: 100, minTableWidth: 1150 }),
                createCellFunction: commission => this.vTableService.createEnumCell(EngineerCommissionStatus, "EngineerCommissionStatus", commission.Status)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Chargeable", width: 110, minTableWidth: 1150 }),
                createCellFunction: commission => this.vTableService.createTableCell(commission.Chargeable ? "Yes" : "No")
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Cost", sortType: SortType.Number, width: 100, minTableWidth: 1250 }),
                createCellFunction: commission => this.vTableService.createTableCell(commission.Chargeable ? commission.Cost : "")
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Commissions.CommissionNumber", sortType: SortType.Number, width: 100, minTableWidth: 500 }),
                createCellFunction: commission => this.vTableService.createTableCell(commission.CommissionNumber > 1 ? commission.CommissionNumber : commission.Id)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerHistory.DeviceValidation", sortable: false, minTableWidth: 1375 }),
                createCellFunction: commission => this.getValidationCheckForCommission(commission)
            }
        ]
    }

    private getResellerName(commission: IEngineerCommission) {
        return this.resellerMap.get(commission.account.ResellerId)?.Description || `AddSecure UK`
    }

    private mapDataToCommissions(): void {
        this.commissions.forEach(commission => {
            this.mapVehicleToCommission(commission);
            this.mapAccountToCommission(commission);
        })
    }

    private mapVehicleToCommission(commission: IEngineerCommission): void {
        commission.vehicle = this.vehiclesMap.get(commission.VehicleId);
    }

    private mapAccountToCommission(commission: IEngineerCommission): void {
        commission.account = this.accountsMap.get(commission.AccountId);
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerCommission>("Common.All", (engineer) => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerCommission>(EngineerJobItemType[EngineerJobItemType.Install], (engineer) => engineer.VisitReasonId == EngineerJobItemType.Install));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerCommission>(EngineerJobItemType[EngineerJobItemType.Uninstall], (engineer) => engineer.VisitReasonId == EngineerJobItemType.Uninstall));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerCommission>(EngineerJobItemType[EngineerJobItemType.Reinstall], (engineer) => engineer.VisitReasonId == EngineerJobItemType.Reinstall));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerCommission>(EngineerJobItemType[EngineerJobItemType.ServiceCall], (engineer) => engineer.VisitReasonId == EngineerJobItemType.ServiceCall));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerCommission>(EngineerJobItemType[EngineerJobItemType.Upgrade], (engineer) => engineer.VisitReasonId == EngineerJobItemType.Upgrade));
    }

    private getValidationCheckForCommission(commission: IEngineerCommission): any {
        const validationObservable = this.vehicleValidationCheckRecordService.getRecordResultsForTable(commission);

        return this.vTableService.createTableCell(commission, {
            component: AsyncObservableComponent,
            componentInputs: {
                observable$: validationObservable,
                useLoading: true
            }
        })
    }
}
